import React, { useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';

import colors from 'utils/colors';

import { ComponentProps, TabsHandle } from './interfaces';
import * as Styled from './styled';

export const Tabs = React.forwardRef<TabsHandle, ComponentProps>(function Tabs(
  { items = [], defaultActiveIndex = 0, onToggleTab },
  ref
) {
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);

  const getTabHandle = useCallback(
    (): TabsHandle => ({
      open: idx => {
        setActiveIndex(idx);
      }
    }),
    [setActiveIndex]
  );

  const handle = useRef<TabsHandle>(getTabHandle());

  // a useLayoutEffect is required here so that the handle instance is updated before the render
  //   else it remains 1 render behind what is the correct index
  useLayoutEffect(() => {
    handle.current = getTabHandle();
  }, [getTabHandle]);

  useImperativeHandle(ref, () => handle.current);

  return (
    <Styled.Wrapper>
      <Styled.Tabs role='tablist'>
        {items.map((item, index) => (
          <Styled.TabItem
            role='tab'
            key={item.id}
            data-testid={`${item.id}-Tabs`}
            onClick={() => {
              setActiveIndex(index);
              if (onToggleTab && index !== activeIndex) {
                onToggleTab(index);
              }
            }}
            active={activeIndex === index}
            aria-selected={activeIndex === index}>
            <Styled.Header>
              <Styled.ArrowIcon fill={activeIndex === index ? colors.orange : colors.blueGrey} />
              <Styled.Title>{item.title}</Styled.Title>
            </Styled.Header>
          </Styled.TabItem>
        ))}
      </Styled.Tabs>
      <Styled.Content role='tabpanel'>{items[activeIndex].component}</Styled.Content>
    </Styled.Wrapper>
  );
});

export default Tabs;
